import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ActivityItem,
  ActivityResponse,
  ApiIssue,
  ListParams,
  ListResult,
  RefMap,
  Resource,
  SummaryParams,
  SummaryResult,
} from '@tnn/core'
import { docStatusOf, toSearchParams } from '@tnn/core'
import type { Option } from '@tnn/ui'
import { useCallback, useMemo } from 'react'
import { api } from './client'

export type Rec = Record<string, unknown> & { id: string }

export interface ImportResult {
  created: number
  updated: number
  failed: { row: number; issues: ApiIssue[] }[]
}

function base(key: string) {
  const [app, name] = key.split('.')
  return `/r/${app}/${name}`
}

/** Typed client for the generic resource endpoints of one resource. */
export function resourceApi(key: string) {
  const path = base(key)
  return {
    list: (params: Partial<ListParams> & { facets?: string[] }) => {
      const sp = toSearchParams(params)
      if (params.facets?.length) sp.set('facets', params.facets.join(','))
      return api.get<ListResult<Rec>>(`${path}?${sp}`)
    },
    get: (id: string) => api.get<{ item: Rec; refs: RefMap }>(`${path}/${encodeURIComponent(id)}`),
    /** Counted and summed per group, for reports (`defineReport`) */
    summary: ({ by, sums, ...params }: SummaryParams) => {
      const sp = toSearchParams(params)
      sp.set('by', by)
      if (sums.length) sp.set('sums', sums.join(','))
      return api.get<SummaryResult>(`${path}/summary?${sp}`)
    },
    /** `submit`: a document issued at once, in the same step (or not at all) */
    create: (body: Record<string, unknown>, options?: { submit?: boolean }) =>
      api.post<{ item: Rec }>(options?.submit ? `${path}?submit=1` : path, body),
    update: (id: string, body: Record<string, unknown>) => api.patch<{ item: Rec }>(`${path}/${encodeURIComponent(id)}`, body),
    remove: (id: string) => api.delete(`${path}/${encodeURIComponent(id)}`),
    /** Document lifecycle (submittable resources): draft → submitted → cancelled → amended draft */
    submit: (id: string) => api.post<{ item: Rec; refs: RefMap }>(`${path}/${encodeURIComponent(id)}/submit`),
    cancel: (id: string, reason?: string) => api.post<{ item: Rec; refs: RefMap }>(`${path}/${encodeURIComponent(id)}/cancel`, { reason }),
    amend: (id: string) => api.post<{ item: Rec; refs: RefMap }>(`${path}/${encodeURIComponent(id)}/amend`),
    /** Logs a print in the record's activity: the how-manyth print this is */
    printed: (id: string) => api.post<{ count: number }>(`${path}/${encodeURIComponent(id)}/printed`),
    import: (body: { rows: Record<string, unknown>[]; mode: 'create' | 'upsert'; dryRun?: boolean }) =>
      api.post<ImportResult>(`${path}/import`, body),
    suggest: (field: string, q: string) =>
      api.get<{ items: { value: string; count: number }[] }>(`${path}/suggest?${new URLSearchParams({ field, q, limit: '10' })}`),
    options: (q: string, ids?: string[], where?: Record<string, string>) => {
      const sp = new URLSearchParams({ q, limit: '20' })
      if (ids?.length) sp.set('ids', ids.join(','))
      else for (const [field, value] of Object.entries(where ?? {})) sp.set(`f.${field}`, value)
      return api.get<{ items: Option[] }>(`${path}/options?${sp}`)
    },
    /** Other records already holding a field's value (`warnTaken`); `alongside`: its `with` fields' values */
    taken: (field: string, value: string, alongside: Record<string, string>, exclude?: string) => {
      const sp = new URLSearchParams({ field, value })
      if (exclude) sp.set('exclude', exclude)
      for (const [name, other] of Object.entries(alongside)) sp.set(`with.${name}`, other)
      return api.get<{ items: Option[]; total: number }>(`${path}/taken?${sp}`)
    },
  }
}

export const queryKeys = {
  resource: (key: string) => ['r', key] as const,
  activity: (key: string, id: string) => ['activity', key, id] as const,
  list: (key: string, params: unknown) => ['r', key, 'list', params] as const,
  record: (key: string, id: string) => ['r', key, 'record', id] as const,
  summary: (key: string, params: unknown) => ['r', key, 'summary', params] as const,
}

export function useResourceList(resource: Resource, params: Partial<ListParams> & { facets?: string[] }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.list(resource.key, params),
    queryFn: () => resourceApi(resource.key).list(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}

/** Counted and summed per group (`summary`): a report's table, an overview's figures */
export function useResourceSummary(resource: Resource, params: SummaryParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.summary(resource.key, params),
    queryFn: () => resourceApi(resource.key).summary(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useResourceRecord(resource: Resource, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.record(resource.key, id ?? ''),
    queryFn: () => resourceApi(resource.key).get(id as string),
    enabled: !!id,
  })
}

/**
 * Create or update, then refresh every list of the resource. An edit after submit acts on other
 * records like submitting does (a payment's allocations settle invoices), so then everything is refreshed.
 */
export function useSaveRecord(resource: Resource) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? resourceApi(resource.key).update(id, values) : resourceApi(resource.key).create(values),
    onSuccess: ({ item }) => {
      if (resource.submittable && docStatusOf(item) === 'submitted') client.invalidateQueries()
      else client.invalidateQueries({ queryKey: queryKeys.resource(resource.key) })
      client.invalidateQueries({ queryKey: queryKeys.activity(resource.key, item.id) })
      client.setQueryData(queryKeys.record(resource.key, item.id), (old: { refs: RefMap } | undefined) => ({ item, refs: old?.refs ?? {} }))
    },
  })
}

export function useDeleteRecord(resource: Resource) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => resourceApi(resource.key).remove(id),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource(resource.key) }),
  })
}

/**
 * Submit, cancel and amend a document. They change other records too (a receipt
 * pays its waybills), so everything cached is refreshed afterwards.
 */
export function useLifecycle(resource: Resource) {
  const client = useQueryClient()
  const done = ({ item, refs }: { item: Rec; refs: RefMap }) => {
    client.setQueryData(queryKeys.record(resource.key, item.id), { item, refs })
    return client.invalidateQueries()
  }
  const api = resourceApi(resource.key)
  return {
    submit: useMutation({ mutationFn: (id: string) => api.submit(id), onSuccess: done }),
    cancel: useMutation({ mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.cancel(id, reason), onSuccess: done }),
    amend: useMutation({ mutationFn: (id: string) => api.amend(id), onSuccess: done }),
  }
}

/** A record's history and comments (`GET …/:id/activity`) */
export function useActivity(resource: Resource, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.activity(resource.key, id ?? ''),
    queryFn: () => api.get<ActivityResponse>(`${base(resource.key)}/${encodeURIComponent(id as string)}/activity`),
    enabled: !!id,
  })
}

/** Leave, edit and delete comments on a record; the activity refreshes after each */
export function useComments(resource: Resource, id: string) {
  const client = useQueryClient()
  const refresh = () => client.invalidateQueries({ queryKey: queryKeys.activity(resource.key, id) })
  return {
    add: useMutation({
      mutationFn: (body: string) => api.post<{ item: ActivityItem }>(`${base(resource.key)}/${encodeURIComponent(id)}/comments`, { body }),
      onSuccess: refresh,
    }),
    edit: useMutation({
      mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
        api.patch<{ item: ActivityItem }>(`/comments/${encodeURIComponent(commentId)}`, { body }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (commentId: string) => api.delete(`/comments/${encodeURIComponent(commentId)}`),
      onSuccess: refresh,
    }),
  }
}

/**
 * Stable loaders for `RemoteSelect` / `RemoteMultiSelect`, cached by react-query.
 * `where` limits the choices (e.g. active vehicles); chosen values always resolve.
 */
export function useOptionsLoader(resourceKey: string, where?: Record<string, string>) {
  const client = useQueryClient()
  const whereKey = JSON.stringify(where ?? {})
  return useMemo(
    () => ({
      load: (q: string) =>
        client
          .fetchQuery({
            queryKey: ['options', resourceKey, whereKey, q],
            queryFn: () => resourceApi(resourceKey).options(q, undefined, JSON.parse(whereKey)),
            staleTime: 30_000,
          })
          .then((r) => r.items),
      resolve: (ids: string[]) =>
        client
          .fetchQuery({
            queryKey: ['options', resourceKey, 'ids', ids],
            queryFn: () => resourceApi(resourceKey).options('', ids),
            staleTime: 60_000,
          })
          .then((r) => r.items),
    }),
    [client, resourceKey, whereKey],
  )
}

/** Stable loader for `SuggestInput` (free-text autocomplete). */
export function useSuggestLoader(resourceKey: string, field: string) {
  const client = useQueryClient()
  return useCallback(
    (q: string) =>
      client
        .fetchQuery({
          queryKey: ['suggest', resourceKey, field, q],
          queryFn: () => resourceApi(resourceKey).suggest(field, q),
          staleTime: 30_000,
        })
        .then((r) => r.items.map((i) => i.value)),
    [client, resourceKey, field],
  )
}
